import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { slugify } from "@lawnops/shared";
import { supabase } from "../lib/supabase";
import { useAuth } from "../auth/AuthProvider";
import { useOrg } from "../auth/OrgProvider";
import { Card, CardHeader } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { AuthLayout } from "./LoginPage";

const schema = z.object({
  name: z.string().min(2),
});
type FormValues = z.infer<typeof schema>;

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh, setActiveOrgId } = useOrg();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { t } = useTranslation(["auth", "common"]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    setSubmitError(null);
    if (!supabase || !user) {
      setSubmitError(t("supabaseNotConfigured"));
      return;
    }
    const slug = `${slugify(values.name)}-${Math.random().toString(36).slice(2, 6)}`;
    const { data: org, error: orgErr } = await supabase
      .from("organizations")
      .insert({ name: values.name, slug })
      .select()
      .single();
    if (orgErr || !org) {
      setSubmitError(orgErr?.message ?? t("onboarding.createError"));
      return;
    }
    const { error: memErr } = await supabase.from("memberships").insert({
      organization_id: org.id,
      user_id: user.id,
      role: "owner",
    });
    if (memErr) {
      setSubmitError(memErr.message);
      return;
    }
    await refresh();
    setActiveOrgId(org.id as string);
    navigate("/", { replace: true });
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader title={t("onboarding.title")} description={t("onboarding.subtitle")} />
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Input
            label={t("fields.companyName")}
            placeholder={t("onboarding.companyPlaceholder")}
            error={errors.name ? t("common:validation.nameMin") : undefined}
            {...register("name")}
          />
          {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}
          <Button type="submit" loading={isSubmitting}>
            {t("buttons.createCompany")}
          </Button>
        </form>
      </Card>
    </AuthLayout>
  );
}
